import React from 'react';
import { DiffEditor } from '@/components/editor/Monaco';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileCode } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-is-mobile';

// Helper to parse hunk content into original and modified strings for the diff editor
const parseHunkContent = (hunkContent: string) => {
  if (!hunkContent) return { original: '', modified: '' };

  const originalLines: string[] = [];
  const modifiedLines: string[] = [];
  const lines = hunkContent.split("\n").filter(line => !line.startsWith(" @@"));

  for (const line of lines) {
    if (line.startsWith("+")) modifiedLines.push(line.substring(1));
    else if (line.startsWith("-")) originalLines.push(line.substring(1));
    else {
      const content = line.startsWith(" ") ? line.substring(1) : line;
      originalLines.push(content);
      modifiedLines.push(content);
    }
  }
  return { original: originalLines.join("\n"), modified: modifiedLines.join("\n") };
};

// Placeholder for a component that would render a diff
export const DiffViewer = ({ files, proposal }: { files: any[] | undefined, proposal: any }) => {
  const isMobile = useIsMobile();
  const [showGitDiff, setShowGitDiff] = React.useState(false);

    if (!files || files.length === 0) {
        return <p>No file changes in this proposal.</p>;
    }

    return (
        <div className={isMobile ? '' : "space-y-4"}>
        <div className="flex items-center space-x-2 mb-4">
          <input
            type="checkbox"
            id="show-git-diff"
            checked={showGitDiff}
            onChange={(e) => setShowGitDiff(e.target.checked)}
          />
          <label htmlFor="show-git-diff">Show actual git diff</label>
        </div>
        {files.map(file => (
            <Card key={file.path} className={isMobile ? 'border-0 shadow-none rounded-none' : ''}>
            <CardHeader className={isMobile ? 'p-2' : ''}>
                <CardTitle className="text-sm font-mono flex items-center gap-2">
                    <FileCode className="h-4 w-4" />
                    {file.path}
                    <Badge variant={file.status === 'modified' ? 'secondary' : 'default'}>{file.status}</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className={isMobile ? 'p-0' : "space-y-2"}>
                {showGitDiff ? (
                  <pre className={`bg-muted text-sm overflow-auto ${isMobile ? 'p-2' : 'p-4 rounded-md'}`}>{file.patch || `No git diff available for ${file.path}`}</pre>
                ) : (
                  file.hunks?.length > 0 ? file.hunks.map((hunk: any, i: number) => {
                      const { original, modified } = parseHunkContent(hunk.content || '');
                      const height = Math.max(original.split('\n').length, modified.split('\n').length) * 19 + 40;
                      const cappedHeight = Math.min(height, 800);

                      return (
                          <div key={i} className={isMobile ? '' : "border rounded-md overflow-auto"}>
                              <DiffEditor
                                  height={cappedHeight}
                                  filePath={file.path}
                                  original={original}
                                  modified={modified}
                                  readOnly={true}
                              />
                          </div>
                      );
                  }) : <p className={`text-sm text-muted-foreground ${isMobile ? 'p-2' : ''}`}>No hunks available for this file.</p>
                )}
            </CardContent>
            </Card>
        ))}
        </div>
    );
};